import { ApiClient } from "@/services/apiClient";
import { type AxiosResponse } from "axios";
import { type CollectionItem_ListItem, type ApiData_UpdateCollectionItem, type CollectionItem, type CollectionItemId, type CreateCollectionItemData, Api_ItemPatchOperation } from "@/services/collection/item/collectionItemTypes";
import type { OnlyPublicProperties } from "@/types";
import type { Filter as CollectionFilter } from "@/services/collection";

/*
 * ===================================================================
 *
 * Collection items fetching.
 * eg. lists, details
 *
 * ===================================================================
 */

export async function fetchCollectionItemById(itemId: CollectionItemId): Promise<CollectionItem> {
  const url = "api/collection/item/" + itemId;
  return ApiClient.get<CollectionItem>(url).then(({ data }) => data);
}

/** @deprecated Use fetchCollectionItemById() */
export async function getCollectionItemById(itemId: CollectionItemId) {
  return fetchCollectionItemById(itemId);
}

type ApiResponse_FetchItemsList = {
  items: CollectionItem_ListItem[];
  totalCount: number;
  totalUnfilteredCount: number;
};

export const fetchCollectionItems = async (filter: OnlyPublicProperties<CollectionFilter.Filter>): Promise<ApiResponse_FetchItemsList> => {
  const url = "api/collection/item";
  const params = filter.createApiParams();
  return ApiClient.get<ApiResponse_FetchItemsList>(url, params).then((response) => response.data);
};

/*
 * ===================================================================
 *
 * Collection items creating.
 *
 * ===================================================================
 */

export async function createCollectionItem(data: CreateCollectionItemData): Promise<CollectionItem> {
  return ApiClient.post<CollectionItem>("api/collection/item", {}, data).then(({ data }) => data);
}

/*
 * ===================================================================
 *
 * Collection items deleting.
 *
 * ===================================================================
 */

export async function deleteCollectionItem(itemId: CollectionItemId): Promise<AxiosResponse> {
  const url = "api/collection/item/" + itemId;
  return ApiClient.delete(url);
}

/*
 * ===================================================================
 *
 * Collection items manipulation.
 * eg. update, archivate, etc.
 *
 * ===================================================================
 */

export async function updateCollectionItem(itemId: CollectionItemId, data: ApiData_UpdateCollectionItem): Promise<CollectionItem> {
  const url = "api/collection/item/" + itemId;
  return ApiClient.put<CollectionItem>(url, {}, data).then(({ data }) => data);
}

export const archivateCollectionItem = async (itemId: CollectionItemId): Promise<AxiosResponse> => {
  const url = "api/collection/item/" + itemId;
  const params = { operation: Api_ItemPatchOperation.Archivate };
  return ApiClient.patch(url, {}, params);
};
